import React, { useMemo, useState } from 'react'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'

import RecordTable from './RecordTable'
import {
  RecordsExplorerDoor,
  usePipelineFrameworksMap,
  useRecordsQuery,
} from '../../hooks/useRecordsQuery'
import { distinctCodingPipelineIds } from '../../utils/recordCodings'

interface RecordsExplorerProps {
  customer: string
  job: string
  /** Exactly one of importBatchId | entityId | pipelineId | codingBatchId. */
  door: RecordsExplorerDoor
  /** Filters codings by state when set. */
  state?: string
  colorMap?: Record<string, string>
  evidenceLabel?: string
  emptyMessage?: string
  /** Set false while the containing tab/dialog is not showing, so it stops polling. */
  active?: boolean
}

/**
 * The shared records view, behind three doors — a receipt's drill-down, an
 * entity's drill-down, a pipeline's Results (and a coding batch's members) —
 * showing the RICHEST version of every record: its raw text when nothing has
 * coded it, and otherwise every coding stacked, each labeled with the pipeline
 * that produced it and rendered through that pipeline's framework.
 *
 * There is deliberately no per-door component: a record must look the same
 * wherever it is reached from, and one component is how that stays true.
 */
const RecordsExplorer: React.FC<RecordsExplorerProps> = ({
  customer,
  job,
  door,
  state,
  colorMap,
  evidenceLabel = 'Match Evidence',
  emptyMessage = 'No records available.',
  active = true,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  const { data, loading, error } = useRecordsQuery({
    customer,
    job,
    door,
    state,
    page,
    rowsPerPage,
    enabled: active,
  })

  const records = useMemo(() => data?.records || [], [data])
  const pipelineIds = useMemo(() => distinctCodingPipelineIds(records), [records])
  const frameworksMap = usePipelineFrameworksMap(customer, job, pipelineIds)

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      data-testid="autocoder-records-explorer"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="info" />
        </Box>
      )}
      {data && records.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
          {emptyMessage}
        </Typography>
      )}
      {data && records.length > 0 && (
        <RecordTable
          data={records}
          totalCount={data.total}
          page={page}
          rowsPerPage={rowsPerPage}
          frameworksMap={frameworksMap}
          colorMap={colorMap}
          evidenceLabel={evidenceLabel}
          onPageChange={setPage}
          onRowsPerPageChange={next => {
            setRowsPerPage(next)
            setPage(0)
          }}
        />
      )}
    </Box>
  )
}

export default RecordsExplorer
